import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  FilterTypeGrid,
  boldFont,
  centerDivContainer,
  checkboxLabel,
  filterAccordionSummary,
} from '../../styles';

//Filter Component
const Filter = props => {
  const { t } = useTranslation();
  const {
    siteListData = [],
    departmentListData = [],
    activeListData = [],
    handleRefreshClick,
    filterMaxHeight = 467,
  } = props;
  const [siteListCheckbox, setSiteListCheckbox] = useState([]);
  const [departmentCheckbox, setDepartmentCheckbox] = useState([]);
  const [activeCheckbox, setActiveCheckbox] = useState([]);

  //To set the siteListCheckbox only once after having value for siteListData
  useEffect(() => {
    if (siteListData.length === 0) return;
    const newSiteList = [];
    siteListData.filter(item => {
      return item.checked ? newSiteList.push(item) : null;
    });
    setSiteListCheckbox(newSiteList);
  }, [siteListData]);

  //To set the departmentCheckbox only once after having value for departmentListData
  useEffect(() => {
    if (departmentListData.length === 0) return;
    const newDepartmentList = [];
    departmentListData.filter(item => {
      return item.checked ? newDepartmentList.push(item) : null;
    });
    setDepartmentCheckbox(newDepartmentList);
  }, [departmentListData]);

  //To set the activeCheckbox only once after having value for activeListData
  useEffect(() => {
    if (activeListData.length === 0) return;
    const newActiveList = [];
    activeListData.filter(item => {
      return item.checked ? newActiveList.push(item) : null;
    });
    setActiveCheckbox(newActiveList);
  }, [activeListData]);

  //To change the selected checkboxes of Sites & Departments into single string
  const filteredUnits = {
    selectedSites: siteListCheckbox.length
      ? siteListCheckbox.map(item => item.SiteName).toString()
      : '',
    selectedDepartment: departmentCheckbox.length
      ? departmentCheckbox.map(item => item.Name).toString()
      : '',
    selectedActive: activeCheckbox.length
      ? activeCheckbox.map(item => item.value).toString()
      : '',
  };

  //To handle the onChange event of Refresh button
  const onRefreshClick = () => {
    handleRefreshClick(filteredUnits);
  };

  //To handle the onChange event of Department checkbox
  const handleDepartmentChange = (event, data) => {
    const value = event.target.value;
    if (value === 'all') {
      setDepartmentCheckbox(
        departmentListData.length === departmentCheckbox.length
          ? []
          : departmentListData.map(item => {
              return {
                Name: item.Name,
                checked: true,
              };
            })
      );
      return;
    }
    const list = [...departmentCheckbox];
    const index = list.map(item => item.Name).indexOf(data.Name);
    index === -1 ? list.push(data) : list.splice(index, 1);
    setDepartmentCheckbox(list);
  };

  //To check whether all checkboxes of Departments are checked or not
  const isAllDepartmentSelected =
    departmentListData.length > 0 &&
    departmentCheckbox.length > 0 &&
    departmentListData.length === departmentCheckbox.length;

  //To handle the onChange event of Site checkbox
  const handleSiteChange = (event, data) => {
    const value = event.target.value;
    if (value === 'all') {
      setSiteListCheckbox(
        siteListData.length === siteListCheckbox.length
          ? []
          : siteListData.map(item => {
              return {
                SiteName: item.SiteName,
                checked: true,
              };
            })
      );
      return;
    }
    const list = [...siteListCheckbox];
    const index = list.map(item => item.SiteName).indexOf(data.SiteName);
    index === -1 ? list.push(data) : list.splice(index, 1);
    setSiteListCheckbox(list);
  };

  //To check whether all checkboxes of Sites are checked or not
  const isAllSiteSelected =
    siteListData.length > 0 &&
    siteListCheckbox.length > 0 &&
    siteListData.length === siteListCheckbox.length;

  //To handle the onChange event of Active checkbox
  const handleActiveChange = (event, data) => {
    const value = event.target.value;
    if (value === 'all') {
      setActiveCheckbox(
        activeListData.length === activeCheckbox.length
          ? []
          : activeListData.map(item => {
              return {
                ...item,
                checked: true,
              };
            })
      );
      return;
    }
    const list = [...activeCheckbox];
    const index = list.map(item => item.value).indexOf(data.value);
    index === -1 ? list.push(data) : list.splice(index, 1);
    setActiveCheckbox(list);
  };

  //To check whether all checkboxes of Active are checked or not
  const isAllActiveSelected =
    activeListData.length > 0 &&
    activeCheckbox.length > 0 &&
    activeListData.length === activeCheckbox.length;

  return (
    <>
      <Button
        variant="contained"
        style={{
          textTransform: 'none',
          width: '300px',
          height: '50px',
          marginBottom: '10px',
          fontSize: '16px',
        }}
        onClick={onRefreshClick}
      >
        {t('Refresh_User_Table')}
      </Button>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Grid
          sx={{
            maxHeight: filterMaxHeight,
            overflowY: 'scroll',
            padding: '10px',
          }}
          container
          width={300}
        >
          <Accordion style={FilterTypeGrid}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="sites-content"
              id="sites-header"
              style={filterAccordionSummary}
            >
              <Typography style={boldFont}>{t('Sites_Impacted')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0px 36px 0px 0px' }}>
              <Grid>
                <Grid style={centerDivContainer}>
                  <Checkbox
                    value="all"
                    onChange={event => handleSiteChange(event, {})}
                    checked={isAllSiteSelected}
                  />
                  <Typography>{t('SELECT_ALL')}</Typography>
                </Grid>
                {siteListData.map((list, i) => {
                  const checked = siteListCheckbox.some(data => {
                    return list.SiteName === data.SiteName;
                  });

                  return (
                    <Grid key={i} style={checkboxLabel}>
                      <Checkbox
                        value={list.SiteName}
                        onChange={event => handleSiteChange(event, list)}
                        checked={checked}
                      />
                      <Grid
                        sx={{
                          position: 'relative',
                          top: 10,
                          display: 'flex',
                          flexDirection: 'row',
                        }}
                      >
                        <Typography>{list.SiteName}</Typography>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion style={FilterTypeGrid}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="status-content"
              id="status-header"
              style={filterAccordionSummary}
            >
              <Typography style={boldFont}>{t('Department')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0px 36px 0px 0px' }}>
              <Grid>
                <Grid style={centerDivContainer}>
                  <Checkbox
                    value="all"
                    onChange={event => handleDepartmentChange(event, {})}
                    checked={isAllDepartmentSelected}
                  />
                  <Typography>{t('SELECT_ALL')}</Typography>
                </Grid>
                {departmentListData.map((list, i) => {
                  const checked = departmentCheckbox.some(data => {
                    return list.Name === data.Name;
                  });

                  return (
                    <Grid key={i} style={checkboxLabel}>
                      <Checkbox
                        value={list.Name}
                        onChange={event => handleDepartmentChange(event, list)}
                        checked={checked}
                      />
                      <Grid
                        sx={{
                          position: 'relative',
                          top: 10,
                          display: 'flex',
                          flexDirection: 'row',
                        }}
                      >
                        <Typography>{list.Name}</Typography>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion style={FilterTypeGrid}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="status-content"
              id="status-header"
              style={filterAccordionSummary}
            >
              <Typography style={boldFont}>{t('Active')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0px 36px 0px 0px' }}>
              <Grid>
                <Grid style={centerDivContainer}>
                  <Checkbox
                    value="all"
                    onChange={event => handleActiveChange(event, {})}
                    checked={isAllActiveSelected}
                  />
                  <Typography>{t('SELECT_ALL')}</Typography>
                </Grid>
                {activeListData.map((list, i) => {
                  const checked = activeCheckbox.some(data => {
                    return list.value === data.value;
                  });

                  return (
                    <Grid key={i} style={checkboxLabel}>
                      <Checkbox
                        value={list.value}
                        onChange={event => handleActiveChange(event, list)}
                        checked={checked}
                      />
                      <Grid
                        sx={{
                          position: 'relative',
                          top: 10,
                          display: 'flex',
                          flexDirection: 'row',
                        }}
                      >
                        <Typography>{list.label}</Typography>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Paper>
    </>
  );
};

export default Filter;
